import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { sanitizePhoneInput, PHONE_MAX_DIGITS } from "@/utils/phoneInput";
import FieldRow from "./FieldRow";

/**
 * Step 1 — lead-gen form: name, email, phone + consent.
 * Pure presentational: receives state + handlers from RegisterDialog.
 */
export default function RegisterStep1({
  form, setForm,
  acceptTerms, setAcceptTerms,
  acceptMarketing, setAcceptMarketing,
  status, error, onSubmit,
}) {
  return (
    <form data-testid={LANDING.leadForm} onSubmit={onSubmit} className="mt-5 space-y-4">
      <FieldRow
        id="name" label="Nome e cognome" testId={LANDING.leadName}
        value={form.name} onChange={(v) => setForm({ ...form, name: v })}
        required minLength={2} placeholder="Es. Mario Rossi"
      />
      <FieldRow
        id="email" type="email" label="Email" testId={LANDING.leadEmail}
        value={form.email} onChange={(v) => setForm({ ...form, email: v })}
        required placeholder="tuo@email.it"
      />
      <FieldRow
        id="phone" type="tel" inputMode="tel"
        label="Cellulare (con prefisso internazionale)" testId={LANDING.leadPhone}
        value={form.phone} onChange={(v) => setForm({ ...form, phone: sanitizePhoneInput(v) })}
        required placeholder="+39 333 1234567"
        maxLength={PHONE_MAX_DIGITS + 6}
      />

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} data-testid="consent-terms" />
          <span className="text-[13px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
            Ho letto e accetto la{" "}
            <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: "var(--np-primary)" }}>Privacy Policy</a>{" "}
            e i{" "}
            <a href="/cookie-policy" target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: "var(--np-primary)" }}>Termini</a>. *
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={acceptMarketing} onCheckedChange={(v) => setAcceptMarketing(!!v)} data-testid="consent-marketing" />
          <span className="text-[13px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
            Acconsento a ricevere comunicazioni di marketing <strong>via email, SMS e WhatsApp</strong> da Alessandro Fulgheri / Neuroscienza Pratica (facoltativo, revocabile in qualsiasi momento scrivendo a <a href="mailto:amministrazione@beetooagency.com" className="underline-offset-2 hover:underline" style={{ color: "var(--np-primary)" }}>amministrazione@beetooagency.com</a>).
          </span>
        </label>
      </div>

      {status === "error" && (
        <p data-testid={LANDING.leadError} className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid={LANDING.leadSubmit}
        disabled={status === "loading" || !acceptTerms}
        className="cta-btn w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "loading" ? (<><Loader2 className="animate-spin" size={18} /> Salvo i tuoi dati…</>) : (<>Continua →</>)}
      </button>

      <div className="flex items-start gap-2 pt-1">
        <ShieldCheck size={14} className="mt-0.5" style={{ color: "var(--np-muted)" }} />
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
          Step 1 di 2 · Nessuna carta richiesta. La prima lezione è 100% gratuita.
        </p>
      </div>
    </form>
  );
}
